import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { Alert, Badge, Breadcrumb, Button, Form, Spinner, Table, Tab, Tabs } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../axios/axiosConfig';
import PdfViewerModal from '../../common/PdfViewerModal';
import { formatSalesCurrency, formatSalesDate } from './salesPageUtils';

const GRAPH_SCOPE = 'https://graph.microsoft.com/.default';
const SALES_QUOTES_TAB = 'sales-quotes';
const ARCHIVE_FOLDER_NAME = 'Archive';
const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg']);

const getProjectSharePointId = (project = {}) =>
  String(
    project.sharePointId ??
      project.SharePointID ??
      project.sharePointID ??
      project.SharepointID ??
      ''
  ).trim();

const getFolderTabKey = (folderId) => `sharepoint-${folderId}`;

const sortDriveItems = (items = []) =>
  [...items].sort((left, right) => {
    const leftIsFolder = Boolean(left.folder);
    const rightIsFolder = Boolean(right.folder);

    if (leftIsFolder !== rightIsFolder) {
      return leftIsFolder ? -1 : 1;
    }

    return String(left.name || '').localeCompare(String(right.name || ''));
  });

const formatFileSize = (bytes) => {
  if (bytes === null || bytes === undefined) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isPdfFile = (fileName = '') => String(fileName).split('.').pop()?.toLowerCase() === 'pdf';

const getFileExtension = (fileName = '') => String(fileName).split('.').pop()?.toLowerCase() || '';

const isImageFile = (fileName = '') => IMAGE_EXTENSIONS.has(getFileExtension(fileName));

const isViewableFile = (fileName = '') => isPdfFile(fileName) || isImageFile(fileName);

const encodeGraphPathSegment = (value) =>
  encodeURIComponent(String(value ?? '').trim()).replace(/%2F/gi, '-');

const getFriendlyGraphError = (fallback) => fallback || 'SharePoint action failed. Please try again.';

const getStatusVariant = (status) => {
  if (status === 'Approved') return 'success';
  if (status === 'ProposalCreated') return 'info';
  if (status === 'Sold') return 'primary';
  return 'secondary';
};

const SalesQuotesTable = ({ project, projectQuotes, isAdmin = false, onDeleteQuote }) => (
  <Table bordered hover size="sm" className="mb-0 sales-expanded-table">
    <thead>
      <tr>
        <th>Quotation Number</th>
        <th>Model Number</th>
        <th>Status</th>
        <th>Created By</th>
        <th>Created Date</th>
        <th>Final Price</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {projectQuotes.length === 0 ? (
        <tr><td colSpan={7}>No quotes for this project.</td></tr>
      ) : (
        projectQuotes.map((quote) => (
          <tr key={quote.id}>
            <td>{quote.quotationNumber}</td>
            <td>{quote.modelNumber}</td>
            <td><Badge bg={getStatusVariant(quote.status)}>{quote.status}</Badge></td>
            <td>{quote.createdBy}</td>
            <td>{formatSalesDate(quote.createdDate)}</td>
            <td>{formatSalesCurrency(quote.finalPrice)}</td>
            <td>
              <div className="d-inline-flex align-items-center" style={{ gap: '10px' }}>
                <button
                  type="button"
                  className="edit-icon"
                  title="Approve"
                  disabled={quote.status !== 'ProposalCreated'}
                  style={{
                    display: 'inline-flex',
                    color: quote.status === 'ProposalCreated' ? '#00457c' : '#cecece',
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                  }}
                >
                  <i className="bi bi-check-circle"></i>
                </button>
                <button
                  type="button"
                  className="edit-icon"
                  title="PDF"
                  disabled={!quote.proposalUrl}
                  style={{
                    display: 'inline-flex',
                    color: quote.proposalUrl ? '#00457c' : '#cecece',
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                  }}
                  onClick={() => quote.proposalUrl && window.open(quote.proposalUrl, '_blank', 'noopener,noreferrer')}
                >
                  <i className="bi bi-file-pdf"></i>
                </button>
                {(quote.statusId === 137 || quote.statusId === 140) ? (
                  <Link to={`/sales/order-sheet/detail/${quote.id}`} className="edit-icon" title="Order Sheet" style={{ display: 'inline-flex', color: '#00457c' }}>
                    <i className="bi bi-receipt"></i>
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="edit-icon"
                    title="Order Sheet"
                    disabled
                    style={{
                      display: 'inline-flex',
                      color: '#cecece',
                      border: 0,
                      background: 'transparent',
                      padding: 0,
                    }}
                  >
                    <i className="bi bi-receipt"></i>
                  </button>
                )}
                <Link to={`/sales/quotes/${quote.id}`} className="edit-icon" title="Edit" style={{ display: 'inline-flex' }}>
                  <i className="bi bi-pencil-square"></i>
                </Link>
                {isAdmin && (
                  <button
                    type="button"
                    className="edit-icon"
                    title="Delete"
                    disabled={quote.statusId !== 139}
                    style={{
                      display: 'inline-flex',
                      color: quote.statusId === 139 ? '#00457c' : '#cecece',
                      border: 0,
                      background: 'transparent',
                      padding: 0,
                    }}
                    onClick={(event) => onDeleteQuote?.(event, quote)}
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
            </td>
          </tr>
        ))
      )}
    </tbody>
  </Table>
);

const SalesProjectSharePointTabs = ({ project, projectQuotes, isAdmin = false, onDeleteQuote }) => {
  const { instance, accounts } = useMsal();
  const siteId = process.env.REACT_APP_SHAREPOINT_SALES_SITE_ID;
  const driveId = process.env.REACT_APP_SHAREPOINT_SALES_DRIVE_ID;
  const projectSharePointId = getProjectSharePointId(project);
  const [activeTab, setActiveTab] = useState(SALES_QUOTES_TAB);
  const [accessToken, setAccessToken] = useState(null);
  const [tokenError, setTokenError] = useState('');
  const [rootFolders, setRootFolders] = useState([]);
  const [rootLoading, setRootLoading] = useState(false);
  const [rootError, setRootError] = useState('');
  const [folderItemsById, setFolderItemsById] = useState({});
  const [folderLoadingId, setFolderLoadingId] = useState('');
  const [folderErrorById, setFolderErrorById] = useState({});
  const [navigationByTab, setNavigationByTab] = useState({});
  const [selectedFileIdsByFolderId, setSelectedFileIdsByFolderId] = useState({});
  const [pendingUploadFilesByFolderId, setPendingUploadFilesByFolderId] = useState({});
  const [folderActionStateById, setFolderActionStateById] = useState({});
  const [folderMessageById, setFolderMessageById] = useState({});
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState('');
  const [pdfExternalUrl, setPdfExternalUrl] = useState('');
  const [pdfViewerTitle, setPdfViewerTitle] = useState('');
  const [viewerType, setViewerType] = useState('pdf');
  const uploadInputRefs = useRef({});

  const hasSharePointConfig = Boolean(siteId && driveId);
  const hasProjectFolder = Boolean(projectSharePointId);

  const folderTabs = useMemo(
    () => rootFolders.map((folder) => ({
      key: getFolderTabKey(folder.id),
      folder,
    })),
    [rootFolders]
  );

  const fetchFolderChildren = useCallback(async (folderId) => {
    const graphUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${folderId}/children?$top=200`;
    const response = await axiosInstance.get(graphUrl, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    return sortDriveItems(response.data.value || []);
  }, [accessToken, driveId, siteId]);

  const loadFolderChildren = useCallback(async (folderId, options = {}) => {
    if (!folderId || (folderItemsById[folderId] && !options.force)) return;

    setFolderLoadingId(folderId);
    setFolderErrorById((current) => ({ ...current, [folderId]: '' }));

    try {
      const items = await fetchFolderChildren(folderId);
      setFolderItemsById((current) => ({ ...current, [folderId]: items }));
      setSelectedFileIdsByFolderId((current) => ({ ...current, [folderId]: [] }));
    } catch (error) {
      console.error('Failed to load Sales project SharePoint folder:', error);
      setFolderErrorById((current) => ({
        ...current,
        [folderId]: 'Unable to load this SharePoint folder.',
      }));
    } finally {
      setFolderLoadingId('');
    }
  }, [fetchFolderChildren, folderItemsById]);

  useEffect(() => {
    let isMounted = true;

    const loadToken = async () => {
      if (!hasSharePointConfig || !hasProjectFolder) return;

      try {
        if (!instance || !accounts || accounts.length === 0) {
          throw new Error('No signed-in account is available for SharePoint.');
        }

        const response = await instance.acquireTokenSilent({
          scopes: [GRAPH_SCOPE],
          account: accounts[0],
        });

        if (isMounted) {
          setAccessToken(response.accessToken);
          setTokenError('');
        }
      } catch (error) {
        console.error('Failed to acquire Sales SharePoint token:', error);
        if (isMounted) {
          setTokenError('Unable to access SharePoint. Please sign in again.');
        }
      }
    };

    loadToken();

    return () => {
      isMounted = false;
    };
  }, [accounts, hasProjectFolder, hasSharePointConfig, instance]);

  useEffect(() => {
    let isMounted = true;

    const loadProjectFolders = async () => {
      if (!accessToken || !hasSharePointConfig || !hasProjectFolder) return;

      setRootLoading(true);
      setRootError('');

      try {
        const children = await fetchFolderChildren(projectSharePointId);
        const folders = children.filter((item) => item.folder);

        if (isMounted) {
          setRootFolders(folders);
          setNavigationByTab((current) => {
            const next = { ...current };
            folders.forEach((folder) => {
              const tabKey = getFolderTabKey(folder.id);
              if (!next[tabKey]) {
                next[tabKey] = [{ id: folder.id, name: folder.name }];
              }
            });
            return next;
          });
        }
      } catch (error) {
        console.error('Failed to load Sales project SharePoint folders:', error);
        if (isMounted) {
          setRootError('Unable to load SharePoint folders for this project.');
        }
      } finally {
        if (isMounted) setRootLoading(false);
      }
    };

    loadProjectFolders();

    return () => {
      isMounted = false;
    };
  }, [accessToken, fetchFolderChildren, hasProjectFolder, hasSharePointConfig, projectSharePointId]);

  const handleTabSelect = async (tabKey) => {
    if (!tabKey) return;

    setActiveTab(tabKey);

    if (tabKey === SALES_QUOTES_TAB) return;

    const breadcrumb = navigationByTab[tabKey] || [];
    const currentFolderId = breadcrumb[breadcrumb.length - 1]?.id || tabKey.replace(/^sharepoint-/, '');
    await loadFolderChildren(currentFolderId);
  };

  const handleFolderClick = async (tabKey, folder) => {
    setNavigationByTab((current) => ({
      ...current,
      [tabKey]: [...(current[tabKey] || []), { id: folder.id, name: folder.name }],
    }));
    await loadFolderChildren(folder.id);
  };

  const handleBreadcrumbClick = async (tabKey, index) => {
    const nextBreadcrumb = (navigationByTab[tabKey] || []).slice(0, index + 1);
    const nextFolderId = nextBreadcrumb[nextBreadcrumb.length - 1]?.id;

    setNavigationByTab((current) => ({
      ...current,
      [tabKey]: nextBreadcrumb,
    }));

    await loadFolderChildren(nextFolderId);
  };

  const setFolderActionState = (folderId, state) => {
    setFolderActionStateById((current) => ({ ...current, [folderId]: state }));
  };

  const setFolderMessage = (folderId, message) => {
    setFolderMessageById((current) => ({ ...current, [folderId]: message }));
  };

  const getSelectedFiles = (folderId) => {
    const selectedIds = new Set(selectedFileIdsByFolderId[folderId] || []);
    return (folderItemsById[folderId] || []).filter((item) => !item.folder && selectedIds.has(item.id));
  };

  const getOrCreateArchiveFolder = async (parentFolderId) => {
    const parentChildren = await fetchFolderChildren(parentFolderId);
    const existingArchiveFolder = parentChildren.find(
      (item) => item.folder && String(item.name || '').trim().toLowerCase() === ARCHIVE_FOLDER_NAME.toLowerCase()
    );

    if (existingArchiveFolder) {
      setFolderItemsById((current) => ({
        ...current,
        [parentFolderId]: sortDriveItems([
          ...(current[parentFolderId] || []).filter((item) => item.id !== existingArchiveFolder.id),
          existingArchiveFolder,
        ]),
      }));
      return existingArchiveFolder;
    }

    const response = await axiosInstance.post(
      `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${parentFolderId}/children`,
      {
        name: ARCHIVE_FOLDER_NAME,
        folder: {},
        '@microsoft.graph.conflictBehavior': 'fail',
      },
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    const createdArchiveFolder = response.data;
    setFolderItemsById((current) => ({
      ...current,
      [parentFolderId]: sortDriveItems([...(current[parentFolderId] || []), createdArchiveFolder]),
    }));
    return createdArchiveFolder;
  };

  const handleToggleFileSelection = (folderId, itemId, checked) => {
    setSelectedFileIdsByFolderId((current) => {
      const existing = new Set(current[folderId] || []);
      if (checked) existing.add(itemId);
      else existing.delete(itemId);
      return { ...current, [folderId]: Array.from(existing) };
    });
  };

  const handleToggleAllFiles = (folderId, files, checked) => {
    setSelectedFileIdsByFolderId((current) => ({
      ...current,
      [folderId]: checked ? files.map((item) => item.id) : [],
    }));
  };

  const handleUploadFileSelection = (folderId, fileList) => {
    setFolderMessage(folderId, '');
    setFolderErrorById((current) => ({ ...current, [folderId]: '' }));
    setPendingUploadFilesByFolderId((current) => ({
      ...current,
      [folderId]: Array.from(fileList || []),
    }));
  };

  const handleUploadFiles = async (folderId) => {
    const files = pendingUploadFilesByFolderId[folderId] || [];
    if (!folderId || files.length === 0 || !accessToken) return;

    setFolderActionState(folderId, 'uploading');
    setFolderMessage(folderId, '');
    setFolderErrorById((current) => ({ ...current, [folderId]: '' }));

    try {
      for (const file of files) {
        const graphUploadUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${folderId}:/${encodeGraphPathSegment(file.name)}:/content`;
        await axiosInstance.put(graphUploadUrl, file, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': file.type || 'application/octet-stream',
          },
        });
      }

      setPendingUploadFilesByFolderId((current) => ({ ...current, [folderId]: [] }));
      if (uploadInputRefs.current[folderId]) {
        uploadInputRefs.current[folderId].value = '';
      }
      setFolderMessage(folderId, `${files.length} file${files.length === 1 ? '' : 's'} uploaded.`);
      await loadFolderChildren(folderId, { force: true });
    } catch (error) {
      console.error('Failed to upload Sales SharePoint files:', error);
      setFolderErrorById((current) => ({
        ...current,
        [folderId]: getFriendlyGraphError('Unable to upload selected files.'),
      }));
    } finally {
      setFolderActionState(folderId, '');
    }
  };

  const handleDeleteSelectedFiles = async (folderId) => {
    const selectedFiles = getSelectedFiles(folderId);
    if (selectedFiles.length === 0 || !accessToken) return;

    const confirmed = window.confirm(`Delete ${selectedFiles.length} selected file${selectedFiles.length === 1 ? '' : 's'}?`);
    if (!confirmed) return;

    setFolderActionState(folderId, 'deleting');
    setFolderMessage(folderId, '');
    setFolderErrorById((current) => ({ ...current, [folderId]: '' }));

    try {
      for (const item of selectedFiles) {
        await axiosInstance.delete(
          `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${item.id}`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );
      }

      setFolderMessage(folderId, `${selectedFiles.length} file${selectedFiles.length === 1 ? '' : 's'} deleted.`);
      await loadFolderChildren(folderId, { force: true });
    } catch (error) {
      console.error('Failed to delete Sales SharePoint files:', error);
      setFolderErrorById((current) => ({
        ...current,
        [folderId]: getFriendlyGraphError('Unable to delete selected files.'),
      }));
    } finally {
      setFolderActionState(folderId, '');
    }
  };

  const handleArchiveFiles = async (folderId, filesToArchive) => {
    const targetFiles = (filesToArchive || []).filter((item) => item && !item.folder);
    if (targetFiles.length === 0 || !accessToken) return;

    setFolderActionState(folderId, 'archiving');
    setFolderMessage(folderId, '');
    setFolderErrorById((current) => ({ ...current, [folderId]: '' }));

    try {
      const resolvedArchiveFolder = await getOrCreateArchiveFolder(folderId);

      if (folderId === resolvedArchiveFolder.id) {
        setFolderMessage(folderId, 'Selected files are already in Archive.');
        return;
      }

      for (const item of targetFiles) {
        await axiosInstance.patch(
          `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${item.id}`,
          {
            parentReference: {
              driveId,
              id: resolvedArchiveFolder.id,
            },
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
      }

      setFolderMessage(folderId, `${targetFiles.length} file${targetFiles.length === 1 ? '' : 's'} moved to Archive.`);
      await loadFolderChildren(folderId, { force: true });
      setFolderItemsById((current) => {
        const next = { ...current };
        delete next[resolvedArchiveFolder.id];
        return next;
      });
    } catch (error) {
      console.error('Failed to archive Sales SharePoint files:', error);
      setFolderErrorById((current) => ({
        ...current,
        [folderId]: getFriendlyGraphError('Unable to move selected files to Archive.'),
      }));
    } finally {
      setFolderActionState(folderId, '');
    }
  };

  const handleArchiveSelectedFiles = async (folderId) => {
    await handleArchiveFiles(folderId, getSelectedFiles(folderId));
  };

  const handleUnarchiveFiles = async (tabKey, folderId, filesToUnarchive) => {
    const targetFiles = (filesToUnarchive || []).filter((item) => item && !item.folder);
    const parentFolderId = navigationByTab[tabKey]?.slice(-2, -1)?.[0]?.id;

    if (targetFiles.length === 0 || !accessToken) return;

    if (!parentFolderId) {
      setFolderErrorById((current) => ({
        ...current,
        [folderId]: 'Unable to identify the parent folder for unarchive.',
      }));
      return;
    }

    setFolderActionState(folderId, 'unarchiving');
    setFolderMessage(folderId, '');
    setFolderErrorById((current) => ({ ...current, [folderId]: '' }));

    try {
      for (const item of targetFiles) {
        await axiosInstance.patch(
          `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${item.id}`,
          {
            parentReference: {
              driveId,
              id: parentFolderId,
            },
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
          }
        );
      }

      setFolderMessage(folderId, `${targetFiles.length} file${targetFiles.length === 1 ? '' : 's'} restored.`);
      await loadFolderChildren(folderId, { force: true });
      if (folderItemsById[parentFolderId]) {
        await loadFolderChildren(parentFolderId, { force: true });
      }
    } catch (error) {
      console.error('Failed to unarchive Sales SharePoint files:', error);
      setFolderErrorById((current) => ({
        ...current,
        [folderId]: getFriendlyGraphError('Unable to restore selected files.'),
      }));
    } finally {
      setFolderActionState(folderId, '');
    }
  };

  const handleUnarchiveSelectedFiles = async (tabKey, folderId) => {
    await handleUnarchiveFiles(tabKey, folderId, getSelectedFiles(folderId));
  };

  const handleDownloadClick = (item) => {
    const downloadUrl = item?.['@microsoft.graph.downloadUrl'] || item?.webUrl;
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const handleOpenWebUrl = (item) => {
    if (item?.webUrl) {
      window.open(item.webUrl, '_blank');
    }
  };

  const handleViewFile = async (item) => {
    if (!item?.id || !accessToken) return;
    const nextViewerType = isImageFile(item.name) ? 'image' : 'pdf';

    setLoadingPdf(true);
    setPdfViewerTitle(item.name || 'SharePoint File');
    setViewerType(nextViewerType);
    setPdfExternalUrl(item.webUrl || item['@microsoft.graph.downloadUrl'] || '');
    setPdfViewerUrl((current) => {
      if (current && current.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return '';
    });
    setShowPdfModal(true);

    try {
      const graphContentUrl = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/items/${item.id}/content`;
      const response = await axiosInstance.get(graphContentUrl, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      setPdfViewerUrl(URL.createObjectURL(response.data));
    } catch (error) {
      console.error('Failed to load Sales SharePoint file preview:', error);
      setPdfViewerUrl('');
    } finally {
      setLoadingPdf(false);
    }
  };

  const handleClosePdfModal = () => {
    setShowPdfModal(false);
    setPdfViewerUrl((current) => {
      if (current && current.startsWith('blob:')) {
        URL.revokeObjectURL(current);
      }
      return '';
    });
    setPdfExternalUrl('');
    setPdfViewerTitle('');
    setViewerType('pdf');
    setLoadingPdf(false);
  };

  useEffect(() => () => {
    if (pdfViewerUrl && pdfViewerUrl.startsWith('blob:')) {
      URL.revokeObjectURL(pdfViewerUrl);
    }
  }, [pdfViewerUrl]);

  const renderSharePointStatus = () => {
    if (!hasSharePointConfig) {
      return (
        <Alert variant="warning" className="mb-0">
          Sales SharePoint configuration is missing. Set REACT_APP_SHAREPOINT_SALES_SITE_ID and REACT_APP_SHAREPOINT_SALES_DRIVE_ID.
        </Alert>
      );
    }

    if (!hasProjectFolder) {
      return <Alert variant="info" className="mb-0">This project does not have a SharePoint folder id.</Alert>;
    }

    if (tokenError) {
      return <Alert variant="danger" className="mb-0">{tokenError}</Alert>;
    }

    if (rootError) {
      return <Alert variant="danger" className="mb-0">{rootError}</Alert>;
    }

    if (rootLoading) {
      return (
        <div className="sales-sharepoint-loading">
          <Spinner animation="border" size="sm" />
          <span>Loading SharePoint folders...</span>
        </div>
      );
    }

    if (rootFolders.length === 0) {
      return <Alert variant="info" className="mb-0">No SharePoint folders found for this project.</Alert>;
    }

    return null;
  };

  const renderFolderTab = (tabKey) => {
    const breadcrumb = navigationByTab[tabKey] || [];
    const currentFolder = breadcrumb[breadcrumb.length - 1];
    const currentFolderId = currentFolder?.id || tabKey.replace(/^sharepoint-/, '');
    const items = folderItemsById[currentFolderId] || [];
    const files = items.filter((item) => !item.folder);
    const selectedIds = new Set(selectedFileIdsByFolderId[currentFolderId] || []);
    const selectedFiles = files.filter((item) => selectedIds.has(item.id));
    const pendingUploadFiles = pendingUploadFilesByFolderId[currentFolderId] || [];
    const actionState = folderActionStateById[currentFolderId] || '';
    const folderMessage = folderMessageById[currentFolderId] || '';
    const isLoading = folderLoadingId === currentFolderId;
    const folderError = folderErrorById[currentFolderId];
    const isActionRunning = Boolean(actionState);
    const allFilesSelected = files.length > 0 && selectedFiles.length === files.length;
    const isArchiveFolder = String(currentFolder?.name || '').trim().toLowerCase() === ARCHIVE_FOLDER_NAME.toLowerCase();

    return (
      <div className="sales-sharepoint-tab">
        {breadcrumb.length > 0 && (
          <Breadcrumb className="sales-sharepoint-breadcrumb">
            {breadcrumb.map((item, index) => (
              <Breadcrumb.Item
                key={item.id}
                active={index === breadcrumb.length - 1}
                onClick={() => index < breadcrumb.length - 1 && handleBreadcrumbClick(tabKey, index)}
              >
                {item.name}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        )}

        {folderError && <Alert variant="danger">{folderError}</Alert>}
        {folderMessage && <Alert variant="success">{folderMessage}</Alert>}

        <div className="sales-sharepoint-action-bar">
          <div className="sales-sharepoint-upload-control">
            <Form.Control
              ref={(element) => {
                if (element) {
                  uploadInputRefs.current[currentFolderId] = element;
                }
              }}
              type="file"
              multiple
              size="sm"
              disabled={isActionRunning || !accessToken}
              onChange={(event) => handleUploadFileSelection(currentFolderId, event.target.files)}
            />
            <Button
              variant="primary"
              size="sm"
              disabled={isActionRunning || pendingUploadFiles.length === 0}
              onClick={() => handleUploadFiles(currentFolderId)}
            >
              {actionState === 'uploading' ? 'Uploading...' : `Upload${pendingUploadFiles.length ? ` (${pendingUploadFiles.length})` : ''}`}
            </Button>
          </div>
          <div className="sales-sharepoint-bulk-actions">
            <span className="text-muted">{selectedFiles.length} selected</span>
            {isArchiveFolder ? (
              <Button
                variant="outline-primary"
                size="sm"
                disabled={isActionRunning || selectedFiles.length === 0}
                onClick={() => handleUnarchiveSelectedFiles(tabKey, currentFolderId)}
              >
                {actionState === 'unarchiving' ? 'Restoring...' : 'Unarchive'}
              </Button>
            ) : (
              <Button
                variant="outline-primary"
                size="sm"
                disabled={isActionRunning || selectedFiles.length === 0}
                onClick={() => handleArchiveSelectedFiles(currentFolderId)}
              >
                {actionState === 'archiving' ? 'Archiving...' : 'Archive'}
              </Button>
            )}
            <Button
              variant="outline-danger"
              size="sm"
              disabled={isActionRunning || selectedFiles.length === 0}
              onClick={() => handleDeleteSelectedFiles(currentFolderId)}
            >
              {actionState === 'deleting' ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="sales-sharepoint-loading">
            <Spinner animation="border" size="sm" />
            <span>Loading folder...</span>
          </div>
        ) : items.length === 0 ? (
          <Alert variant="info" className="mb-0">No files or folders found.</Alert>
        ) : (
          <Table bordered hover size="sm" className="mb-0 sales-sharepoint-table">
            <thead>
              <tr>
                <th style={{ width: 44 }}>
                  <Form.Check
                    type="checkbox"
                    checked={allFilesSelected}
                    disabled={files.length === 0 || isActionRunning}
                    onChange={(event) => handleToggleAllFiles(currentFolderId, files, event.target.checked)}
                    aria-label="Select all files"
                  />
                </th>
                <th>Name</th>
                <th>Type</th>
                <th>Modified</th>
                <th>Size</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {!item.folder && (
                      <Form.Check
                        type="checkbox"
                        checked={selectedIds.has(item.id)}
                        disabled={isActionRunning}
                        onChange={(event) => handleToggleFileSelection(currentFolderId, item.id, event.target.checked)}
                        aria-label={`Select ${item.name}`}
                      />
                    )}
                  </td>
                  <td>
                    {item.folder ? (
                      <button
                        type="button"
                        className="sales-sharepoint-item-button"
                        onClick={() => handleFolderClick(tabKey, item)}
                      >
                        <i className="bi bi-folder me-2"></i>
                        {item.name}
                      </button>
                    ) : (
                      <span>
                        <i className="bi bi-file-earmark me-2"></i>
                        {item.name}
                      </span>
                    )}
                  </td>
                  <td>{item.folder ? 'Folder' : item.file?.mimeType || 'File'}</td>
                  <td>{item.lastModifiedDateTime ? new Date(item.lastModifiedDateTime).toLocaleDateString() : '-'}</td>
                  <td>{item.folder ? '-' : formatFileSize(item.size)}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-2">
                      {item.folder ? (
                        <Button variant="outline-primary" size="sm" onClick={() => handleFolderClick(tabKey, item)}>
                          Open
                        </Button>
                      ) : (
                        <>
                          {isViewableFile(item.name) && (
                            <Button variant="primary" size="sm" onClick={() => handleViewFile(item)}>
                              View
                            </Button>
                          )}
                          <Button variant="outline-secondary" size="sm" onClick={() => handleDownloadClick(item)}>
                            Download
                          </Button>
                          {item.webUrl && (
                            <Button variant="outline-primary" size="sm" onClick={() => handleOpenWebUrl(item)}>
                              Open
                            </Button>
                          )}
                          {isArchiveFolder ? (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={isActionRunning}
                              onClick={() => handleUnarchiveFiles(tabKey, currentFolderId, [item])}
                            >
                              Unarchive
                            </Button>
                          ) : (
                            <Button
                              variant="outline-primary"
                              size="sm"
                              disabled={isActionRunning}
                              onClick={() => handleArchiveFiles(currentFolderId, [item])}
                            >
                              Archive
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </div>
    );
  };

  return (
    <div className="sales-project-expanded-tabs">
      <Tabs activeKey={activeTab} onSelect={handleTabSelect} className="sales-project-tabs mb-3">
        <Tab eventKey={SALES_QUOTES_TAB} title={`Sales Quotes (${projectQuotes.length})`}>
        <SalesQuotesTable project={project} projectQuotes={projectQuotes} isAdmin={isAdmin} onDeleteQuote={onDeleteQuote} />
        </Tab>
        {folderTabs.map(({ key, folder }) => (
          <Tab eventKey={key} title={folder.name} key={key}>
            {renderFolderTab(key)}
          </Tab>
        ))}
      </Tabs>

      {renderSharePointStatus()}

      <PdfViewerModal
        show={showPdfModal}
        onHide={handleClosePdfModal}
        title={pdfViewerTitle}
        loading={loadingPdf}
        viewerUrl={pdfViewerUrl}
        externalUrl={pdfExternalUrl}
        viewerType={viewerType}
        dialogClassName="sales-pdf-viewer-modal"
      />
    </div>
  );
};

export default SalesProjectSharePointTabs;
